/**
 * useAdaptiveGridData
 *
 * THE standard for the client/server filtering split (ratified 2026-07-09). A probe/full-set
 * query (`pageSize = threshold`) always runs; once it FULLY resolves, `isClient` flips true iff
 * the total is within `threshold` — never on a partial/loading set (the hard invariant). A
 * parallel server-mode page query is enabled only when not in client mode.
 *
 * Client mode runs filter -> search -> sort -> page-slice in memory using the same op
 * semantics as the server path (`makeFilterPredicate`-equivalent `clientPredicate`,
 * `makeSortComparator`), so results never diverge between modes. Search is debounced 300ms
 * in both modes.
 *
 * See `.github/context/list-grid-standard-behaviours.context.md` §10.
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type SortState, makeSortComparator } from './useTableSort';
import { useDebouncedValue } from './useDebouncedValue';

export const ADAPTIVE_GRID_THRESHOLD = 1000;

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ListQueryParams {
  page: number;
  pageSize: number;
  search?: string;
  filters?: string;
}

export interface UseAdaptiveGridDataOptions<T> {
  /** Query-key prefix for this list — e.g. `jobKeys.list(scopeParams)`. */
  queryKeyBase: readonly unknown[];
  /** Fetches one page from the server; also used for the full-set probe (pageSize = threshold). */
  fetchPage: (params: ListQueryParams) => Promise<PagedResult<T>>;
  page: number;
  pageSize: number;
  search?: string;
  /** Serialized column filters (per §8) — sent to the server in server mode. */
  filtersSerialized?: string;
  sortState?: SortState[];
  /** Client-mode-only in-memory predicate (from `useColumnFilters`). */
  clientPredicate?: (item: T) => boolean;
  /** Declared client-mode search fields — case-insensitive `contains`. */
  searchFields?: (keyof T)[];
  /** Custom client-mode search predicate — overrides `searchFields` when provided. */
  searchPredicate?: (item: T, search: string) => boolean;
  /** Overridable per-grid; defaults to `ADAPTIVE_GRID_THRESHOLD`. */
  threshold?: number;
  /** Field accessor for the sort comparator; defaults to a plain index lookup. */
  getValue?: (item: T, field: string) => unknown;
  enabled?: boolean;
}

export interface UseAdaptiveGridDataResult<T> {
  items: T[];
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  isClientMode: boolean;
  refetch: () => void;
}

export function useAdaptiveGridData<T>(
  options: UseAdaptiveGridDataOptions<T>
): UseAdaptiveGridDataResult<T> {
  const threshold = options.threshold ?? ADAPTIVE_GRID_THRESHOLD;
  const enabled = options.enabled ?? true;
  const debouncedSearch = useDebouncedValue(options.search ?? '', 300);

  // Probe/full-set query — always runs; drives the client/server mode decision.
  const probeQuery = useQuery({
    queryKey: [...options.queryKeyBase, 'adaptive-probe', threshold],
    queryFn: () => options.fetchPage({ page: 1, pageSize: threshold }),
    enabled,
    placeholderData: keepPreviousData,
  });

  // Only flips to client mode once the probe has FULLY resolved — never on a partial set.
  const isClientMode = probeQuery.data !== undefined && probeQuery.data.totalCount <= threshold;

  const serverQuery = useQuery({
    queryKey: [
      ...options.queryKeyBase,
      'adaptive-page',
      options.page,
      options.pageSize,
      debouncedSearch,
      options.filtersSerialized,
    ],
    queryFn: () =>
      options.fetchPage({
        page: options.page,
        pageSize: options.pageSize,
        search: debouncedSearch || undefined,
        filters: options.filtersSerialized || undefined,
      }),
    enabled: enabled && !isClientMode && probeQuery.data !== undefined,
    placeholderData: keepPreviousData,
  });

  const clientResult = useMemo(() => {
    if (!isClientMode || !probeQuery.data) return null;

    let rows = probeQuery.data.items;

    if (options.clientPredicate) {
      rows = rows.filter(options.clientPredicate);
    }

    if (debouncedSearch) {
      const needle = debouncedSearch.toLowerCase();
      rows = rows.filter((item) => {
        if (options.searchPredicate) return options.searchPredicate(item, debouncedSearch);
        if (options.searchFields && options.searchFields.length > 0) {
          return options.searchFields.some((field) =>
            String((item as Record<string, unknown>)[field as string] ?? '')
              .toLowerCase()
              .includes(needle)
          );
        }
        return true;
      });
    }

    if (options.sortState && options.sortState.length > 0) {
      rows = [...rows].sort(makeSortComparator(options.sortState, options.getValue));
    }

    const totalCount = rows.length;
    const totalPages = Math.max(Math.ceil(totalCount / options.pageSize), 1);
    const start = (options.page - 1) * options.pageSize;

    return {
      items: rows.slice(start, start + options.pageSize),
      totalCount,
      totalPages,
      hasPreviousPage: options.page > 1,
      hasNextPage: options.page < totalPages,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isClientMode,
    probeQuery.data,
    options.clientPredicate,
    debouncedSearch,
    options.sortState,
    options.page,
    options.pageSize,
    options.searchFields,
    options.searchPredicate,
    options.getValue,
  ]);

  if (isClientMode && clientResult) {
    return {
      items: clientResult.items,
      totalCount: clientResult.totalCount,
      totalPages: clientResult.totalPages,
      hasPreviousPage: clientResult.hasPreviousPage,
      hasNextPage: clientResult.hasNextPage,
      isLoading: probeQuery.isLoading,
      isFetching: probeQuery.isFetching,
      isError: probeQuery.isError,
      error: probeQuery.error,
      isClientMode: true,
      refetch: () => {
        void probeQuery.refetch();
      },
    };
  }

  return {
    items: serverQuery.data?.items ?? [],
    totalCount: serverQuery.data?.totalCount ?? 0,
    totalPages: serverQuery.data?.totalPages ?? 1,
    hasPreviousPage: serverQuery.data?.hasPreviousPage ?? false,
    hasNextPage: serverQuery.data?.hasNextPage ?? false,
    isLoading: probeQuery.isLoading || serverQuery.isLoading,
    isFetching: probeQuery.isFetching || serverQuery.isFetching,
    isError: probeQuery.isError || serverQuery.isError,
    error: serverQuery.error ?? probeQuery.error,
    isClientMode: false,
    refetch: () => {
      void probeQuery.refetch();
      void serverQuery.refetch();
    },
  };
}
